import { Fragment } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ChatPerson } from "../../api/chat";
import { findWords, isEmojiOnly, parseBody } from "./chatFormat";
import type { Block, Inline } from "./chatFormat";

/** What drawing a message needs besides the message: who is who, the searched words, and whether links are text. */
type Draw = {
  people: Map<string, ChatPerson>;
  me: string;
  t: (key: string, fallback: string) => string;
  words: string[];
  plain: boolean;
};

/**
 * A message's text, formatted. Every run becomes an element; nothing is set as HTML. `suffix` (the "edited" mark)
 * closes the last line of text, as Slack writes it, rather than taking a line of its own. `words` are marked where
 * they stand, for a search; `plain` draws links as text, for a message that is itself a link.
 */
export function MessageBody({ body, people, me, suffix, words = [], plain = false }: {
  body: string;
  people: Map<string, ChatPerson>;
  me: string;
  suffix?: ReactNode;
  words?: string[];
  plain?: boolean;
}) {
  const { t } = useTranslation();
  const d: Draw = { people, me, t, words, plain };
  if (!body.trim()) return suffix ? <div className="chat-msg-body">{suffix}</div> : null;
  if (isEmojiOnly(body)) return <div className="chat-msg-body jumbo">{body.trim()}{suffix}</div>;
  const blocks = parseBody(body);
  const lastText = blocks.length > 0 && blocks[blocks.length - 1].kind === "text";
  return (
    <div className="chat-msg-body">
      {blocks.map((block, i) => (
        <Fragment key={i}>{drawBlock(block, d, lastText && i === blocks.length - 1 ? suffix : null)}</Fragment>
      ))}
      {!lastText && suffix}
    </div>
  );
}

/** The text with the searched words in it marked. */
export function Marked({ text, words }: { text: string; words: string[] }) {
  const found = findWords(text, words);
  if (found.length === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let at = 0;
  found.forEach(([start, end], i) => {
    if (start > at) parts.push(text.slice(at, start));
    parts.push(<mark key={i}>{text.slice(start, end)}</mark>);
    at = end;
  });
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

function drawBlock(block: Block, d: Draw, suffix: ReactNode): ReactNode {
  switch (block.kind) {
    case "pre":
      return <pre><Marked text={block.text} words={d.words} /></pre>;
    case "quote":
      return <blockquote><p>{drawRuns(block.inline, d)}</p></blockquote>;
    case "list": {
      const items = block.items.map((item, j) => <li key={j}>{drawRuns(item, d)}</li>);
      return block.ordered ? <ol start={block.start}>{items}</ol> : <ul>{items}</ul>;
    }
    default:
      return <p>{drawRuns(block.inline, d)}{suffix}</p>;
  }
}

function drawRuns(runs: Inline[], d: Draw): ReactNode {
  return runs.map((run, i) => <Fragment key={i}>{drawRun(run, d)}</Fragment>);
}

function drawRun(run: Inline, d: Draw): ReactNode {
  switch (run.kind) {
    case "text":
      return <Marked text={run.text} words={d.words} />;
    case "code":
      return <code><Marked text={run.text} words={d.words} /></code>;
    case "bold":
      return <strong>{drawRuns(run.children, d)}</strong>;
    case "italic":
      return <em>{drawRuns(run.children, d)}</em>;
    case "strike":
      return <s>{drawRuns(run.children, d)}</s>;
    case "link":
      return d.plain ? (
        <span className="chat-link">{drawRuns(run.children, d)}</span>
      ) : (
        <a href={run.href} target="_blank" rel="noopener noreferrer">
          {drawRuns(run.children, d)}
        </a>
      );
    case "mention": {
      const person = d.people.get(run.id);
      const name = person?.name ?? d.t("chat.unknownPerson", "former colleague");
      // A search finds a message by the name of a colleague it names, so the name is marked as a whole.
      const hit = d.words.length > 0 && findWords(name, d.words).length > 0;
      return (
        <span className={`chat-mention${run.id === d.me ? " me" : ""}${hit ? " hit" : ""}`}>
          @{name}
        </span>
      );
    }
  }
}
